import React, { useEffect, useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { X, Copy, Check, ArrowLeft, Plug, ExternalLink, Users } from "lucide-react";
import { api } from "@/platform/api";
import { FullScreenLoader, Button, ACCENT } from "@/platform/components/primitives";
import GettingStarted from "@/platform/app/GettingStarted";
import TicketsBoard from "@/platform/app/TicketsBoard";
import WidgetSettings from "@/platform/app/WidgetSettings";
import ProjectReview from "@/platform/app/ProjectReview";
import ProjectTeam from "@/platform/app/ProjectTeam";
import InviteReviewerModal from "@/platform/app/InviteReviewerModal";

const TABS = ["Overview", "Review", "Tickets", "Board", "Team", "Integrations", "Settings"];
const BACKEND = process.env.REACT_APP_BACKEND_URL;

function ConnectModal({ project, onClose }) {
  const [copied, setCopied] = useState(false);
  const snippet = `<script\n  src="${BACKEND}/api/embed/v1.js"\n  data-project="${project.projectKey}">\n</script>`;
  const previewUrl = `${BACKEND}/api/embed/demo-host?project=${project.projectKey}`;
  const copy = () => { navigator.clipboard?.writeText(snippet); setCopied(true); setTimeout(() => setCopied(false), 1800); };
  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center px-5" data-testid="connect-modal">
      <div className="absolute inset-0 bg-black/55 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-[520px] rounded-2xl border border-[#242A38] bg-[#151A24] p-7 shadow-2xl">
        <button onClick={onClose} className="absolute right-4 top-4 text-[#6E7686] hover:text-[#AAB1BF]" aria-label="Close"><X className="h-5 w-5" /></button>
        <h2 className="text-[20px] font-semibold tracking-tight text-[#F4F5F8]">Install Pinlayr</h2>
        <p className="mt-1.5 text-[14px] text-[#98A0B0]">Add this snippet before the closing &lt;/body&gt; tag on your site. Feedback pinned on the page lands in Tickets & Board automatically.</p>
        <pre className="mt-4 overflow-x-auto rounded-xl bg-[#111111] p-4 text-[12.5px] leading-relaxed text-[#F4F5F8]"><code data-testid="install-snippet">{snippet}</code></pre>
        <div className="mt-3 flex flex-wrap items-center gap-3">
          <Button onClick={copy} className="sm:w-auto sm:px-5" data-testid="copy-snippet">{copied ? <><Check className="h-4 w-4" /> Copied</> : <><Copy className="h-4 w-4" /> Copy code</>}</Button>
          <a href={previewUrl} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-[13px] font-medium text-[#A99BFF] hover:underline" data-testid="preview-widget-link"><ExternalLink className="h-4 w-4" /> Try it on a test page</a>
        </div>
        <p className="mt-4 rounded-lg bg-[#0E1016] px-3.5 py-2.5 text-[12.5px] text-[#6E7686]">The test page is a sample site with Pinlayr pre-installed — click the <b className="text-[#A99BFF]">Review this page</b> control, enter review mode and drop a pin to see it land in Tickets & Board.</p>
      </div>
    </div>
  );
}

function TabEmpty({ label }) {
  return (
    <div className="rounded-2xl border border-dashed border-[#2A3040] bg-[#151A24] p-14 text-center">
      <p className="text-[15px] font-medium text-[#C4C9D4]">{label}</p>
      <p className="mt-1 text-[13.5px] text-[#6E7686]">Coming in the next phase.</p>
    </div>
  );
}

export default function ProjectView() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const deepTicket = searchParams.get("ticket");
  const [data, setData] = useState(null);
  const [tab, setTab] = useState(searchParams.get("tab") || "Overview");
  const [connect, setConnect] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [verifyMsg, setVerifyMsg] = useState("");
  const [invite, setInvite] = useState(false);
  const [activeInvites, setActiveInvites] = useState(null);
  const [reviewReload, setReviewReload] = useState(0);

  const loadInvites = React.useCallback(async () => {
    try {
      const res = await api.get(`/api/projects/${projectId}/reviewer-invites`);
      setActiveInvites((res.invites || []).filter((i) => i.status === "active").length);
    } catch (_e) { /* ignore */ }
  }, [projectId]);
  useEffect(() => { loadInvites(); }, [loadInvites]);

  const load = React.useCallback(async () => {
    try { setData(await api.get(`/api/projects/${projectId}`)); }
    catch (_e) { navigate("/app", { replace: true }); }
  }, [projectId, navigate]);

  useEffect(() => { load(); }, [load]);

  const verify = async () => {
    setVerifying(true); setVerifyMsg("");
    try {
      const res = await api.post(`/api/projects/${projectId}/verify-connection`);
      if (res.connected) { setVerifyMsg("connected"); await load(); }
      else setVerifyMsg("waiting");
    } catch (_e) { setVerifyMsg("waiting"); }
    setVerifying(false);
  };

  if (!data) return <FullScreenLoader label="Loading project" />;
  const { project, sites, checklist } = data;
  const site = sites?.[0];
  const connected = site?.connectionStatus === "connected";

  return (
    <div className="animate-in fade-in duration-300">
      <button onClick={() => navigate("/app")} className="mb-4 inline-flex items-center gap-1.5 text-[13px] text-[#98A0B0] hover:text-[#F4F5F8]"><ArrowLeft className="h-4 w-4" /> All projects</button>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-[24px] font-semibold tracking-tight text-[#F4F5F8]" data-testid="project-title">{project.name}</h1>
          <p className="mt-0.5 text-[14px] text-[#6E7686]">{site?.domain}</p>
        </div>
        {connected ? (
          <span className="inline-flex items-center gap-1.5 rounded-full bg-[#16A34A22] px-3 py-1.5 text-[12px] font-semibold text-[#4ADE80]" data-testid="connection-status"><span className="h-1.5 w-1.5 rounded-full bg-[#4ADE80]" /> Connected</span>
        ) : (
          <span className="rounded-full bg-[#1C2130] px-3 py-1.5 text-[12px] font-medium text-[#98A0B0]" data-testid="connection-status">Not connected</span>
        )}
      </div>

      <div className="mt-5 flex gap-1 overflow-x-auto border-b border-[#242A38]">
        {TABS.map((t) => (
          <button key={t} onClick={() => setTab(t)} className={`whitespace-nowrap border-b-2 px-3.5 py-2.5 text-[13.5px] font-medium transition-colors ${tab === t ? "border-[#6D5EF6] text-[#F4F5F8]" : "border-transparent text-[#6E7686] hover:text-[#AAB1BF]"}`} data-testid={`tab-${t.toLowerCase()}`}>{t}</button>
        ))}
      </div>

      <div className="mt-6">
        {tab === "Overview" ? (
          <div className="space-y-8">
            <div className="flex flex-wrap items-center gap-4 rounded-2xl border border-[#242A38] bg-[#151A24] px-6 py-4" data-testid="review-access-card">
              <div className="grid h-10 w-10 place-items-center rounded-xl" style={{ background: "rgba(124,107,255,0.18)" }}><Users className="h-5 w-5" style={{ color: "#A99BFF" }} /></div>
              <div>
                <p className="text-[15px] font-semibold text-[#F4F5F8]">Review access</p>
                <p className="text-[13px] text-[#98A0B0]" data-testid="review-access-count">
                  {activeInvites === null ? "…" : activeInvites === 0 ? "No reviewer links yet" : `${activeInvites} active reviewer ${activeInvites === 1 ? "link" : "links"}`}
                </p>
              </div>
              <div className="ml-auto"><Button onClick={() => setInvite(true)} className="sm:w-auto sm:px-5" data-testid="invite-reviewer-btn">Invite reviewer</Button></div>
            </div>
            <div className="grid gap-8 lg:grid-cols-[1fr_360px]">
            <div className="rounded-2xl border border-[#242A38] bg-[#151A24] p-8">
              {connected ? (
                <>
                  <div className="grid h-12 w-12 place-items-center rounded-xl" style={{ background: "rgba(22,163,74,0.16)" }}><Check className="h-6 w-6" style={{ color: "#4ADE80" }} /></div>
                  <h2 className="mt-5 text-[18px] font-semibold text-[#F4F5F8]">Pinlayr is live on {site?.domain}</h2>
                  <p className="mt-1.5 max-w-md text-[14px] text-[#98A0B0]">The review engine is installed and reporting in. Open the site and use the Pinlayr control to pin feedback — it lands in Tickets & Board.</p>
                  <div className="mt-6 flex flex-wrap gap-3">
                    <Button variant="outline" onClick={() => setConnect(true)} className="sm:w-auto sm:px-5" data-testid="view-snippet-btn">View install snippet</Button>
                    <Button variant="outline" onClick={verify} loading={verifying} className="sm:w-auto sm:px-5" data-testid="verify-connection-btn">Re-check connection</Button>
                  </div>
                </>
              ) : (
                <>
                  <div className="grid h-12 w-12 place-items-center rounded-xl" style={{ background: "rgba(124,107,255,0.18)" }}><Plug className="h-6 w-6" style={{ color: "#A99BFF" }} /></div>
                  <h2 className="mt-5 text-[18px] font-semibold text-[#F4F5F8]">Connect Pinlayr to {site?.domain}</h2>
                  <p className="mt-1.5 max-w-md text-[14px] text-[#98A0B0]">Install the Pinlayr snippet on your website, then verify — the review engine appears on your live pages and feedback becomes tickets.</p>
                  <div className="mt-6 flex flex-wrap gap-3">
                    <Button onClick={() => setConnect(true)} className="sm:w-auto sm:px-6" data-testid="connect-btn">Connect Pinlayr</Button>
                    <Button variant="outline" onClick={verify} loading={verifying} className="sm:w-auto sm:px-5" data-testid="verify-connection-btn">Verify connection</Button>
                  </div>
                  {verifyMsg === "waiting" && <p className="mt-3 text-[13px] text-[#E5A400]" data-testid="verify-waiting">No signal yet. Make sure the snippet is installed and the page has been opened at least once, then try again.</p>}
                </>
              )}
            </div>
            <GettingStarted checklist={checklist} />
          </div>
          </div>
        ) : tab === "Review" ? (
          <ProjectReview
            project={project}
            sites={sites}
            connected={connected}
            onOpenConnect={() => setConnect(true)}
            onOpenInvite={() => setInvite(true)}
            onVerify={verify}
            verifying={verifying}
            verifyMsg={verifyMsg}
            onCustomizeWidget={() => setTab("Settings")}
            reloadToken={reviewReload}
          />
        ) : tab === "Tickets" ? (
          <TicketsBoard projectId={project.id} domain={site?.domain} initialView="list" initialTicketId={deepTicket} />
        ) : tab === "Board" ? (
          <TicketsBoard projectId={project.id} domain={site?.domain} initialView="board" initialTicketId={deepTicket} />
        ) : tab === "Settings" ? (
          <WidgetSettings project={project} onSaved={setData} />
        ) : tab === "Team" ? (
          <ProjectTeam project={project} />
        ) : (
          <TabEmpty label={tab} />
        )}
      </div>

      {connect && <ConnectModal project={project} onClose={() => setConnect(false)} />}
      {invite && <InviteReviewerModal project={project} onClose={() => setInvite(false)} onChanged={() => { loadInvites(); load(); setReviewReload((n) => n + 1); }} />}
    </div>
  );
}
